// SIMPLIFIED BIKE RETURNS - SINGLE DATA SOURCE
// Enhanced with SHARED Invoice Service for Extended Invoices

import React, { useState, useEffect } from "react";
import { XCircle, AlertCircle, Loader, Building, Shield, Database, Bike } from "lucide-react";
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// Import the SHARED Firebase Invoice Service
import {
  addInvoice,
  generateInvoiceNumber,
  setCompanyContext
} from '../services/firebaseInvoiceService';

// Simplified BikeReturns with single data source
const SimplifiedBikeReturns = ({ 
  currentUser,
  currentCompany, 
  getCompanyContext
}) => {
  // Get company context for data isolation
  const companyId = getCompanyContext ? getCompanyContext() : null;

  // State Management
  const [bikes, setBikes] = useState([]);
  const [bikeDetailsMap, setBikeDetailsMap] = useState(new Map());
  const [dueTodayReservations, setDueTodayReservations] = useState([]);
  const [overdueReservations, setOverdueReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Firebase collections - SIMPLIFIED: Only need these
  const managedBikesCollection = collection(db, 'managedBikes');
  const reservationsCollection = collection(db, `companies/${companyId}/reservations`);
  const incomeCollection = collection(db, `companies/${companyId}/income`);

  // Permission check
  const hasPermission = (permission) => {
    if (currentUser?.permissions?.includes(permission)) return true;
    if (currentUser?.role === 'admin') return true;
    return false;
  };

  // Permission guard
  if (!hasPermission('bookings')) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <XCircle className="w-5 h-5 mr-2 text-orange-400" />
            Bike Returns
            <span className="ml-2 text-xs bg-red-600 text-white px-2 py-1 rounded-full">
              Access Denied
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <Shield className="mx-auto h-20 w-20 text-gray-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Access Denied</h3>
          <p className="text-gray-500 text-lg">You don't have permission to process bike returns.</p>
        </div>
      </div>
    );
  }

  // Company context validation
  if (!companyId || !currentCompany) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <XCircle className="w-5 h-5 mr-2 text-orange-400" />
            Bike Returns
            <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
              Simplified
            </span>
          </h2>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-20 w-20 text-yellow-400 mb-6" />
          <h3 className="text-2xl font-semibold text-gray-300 mb-4">Company Context Missing</h3>
          <p className="text-gray-500 text-lg">Please ensure you're logged in with a valid company.</p>
        </div>
      </div>
    );
  }

  // Load company bikes
  useEffect(() => {
    if (!companyId) return;

    console.log(`Loading bikes for company: ${currentCompany.name} (${companyId})`);
    
    const q = query(managedBikesCollection, where('companyId', '==', companyId));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const loadedBikes = [];
      const detailsMap = new Map();
      
      querySnapshot.forEach((doc) => {
        const bikeData = { id: doc.id, ...doc.data() };
        loadedBikes.push(bikeData);
        
        // Create mapping for quick lookups
        detailsMap.set(bikeData.regNumber, bikeData);
        detailsMap.set(bikeData.id, bikeData);
        detailsMap.set(bikeData.model, bikeData);
      });
      
      setBikes(loadedBikes);
      setBikeDetailsMap(detailsMap);
      
      console.log(`Loaded ${loadedBikes.length} bikes for ${currentCompany.name}`);
    }, (error) => {
      console.error('Error loading bikes:', error);
    });

    return () => unsubscribe();
  }, [companyId]);

  // SIMPLIFIED: Load returns from reservations only
  useEffect(() => {
    if (!companyId) return;

    console.log(`Loading simplified returns for: ${currentCompany.name}`);
    
    // Get today's date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayString = today.toISOString().split('T')[0];
    
    // Query for booked reservations
    const q = query(
      reservationsCollection,
      where('availabilityStatus', '==', 'booked')
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const allReservations = [];
      querySnapshot.forEach((doc) => {
        allReservations.push({ id: doc.id, ...doc.data() });
      });
      
      // Filter for due today and overdue (not completed)
      const dueTodayFiltered = [];
      const overdueFiltered = [];
      
      allReservations.forEach(reservation => {
        // Skip cancelled or completed reservations
        if (reservation.availabilityStatus === 'cancel' || 
            reservation.rentalStatus === 'completed') {
          return;
        }
        
        if (reservation.dropoffDateTime) {
          const returnDate = new Date(reservation.dropoffDateTime);
          returnDate.setHours(0, 0, 0, 0);
          const returnDateString = returnDate.toISOString().split('T')[0];
          
          if (returnDateString === todayString) {
            dueTodayFiltered.push(reservation);
          } else if (returnDate < today) {
            overdueFiltered.push(reservation);
          }
        }
      });
      
      setDueTodayReservations(dueTodayFiltered);
      setOverdueReservations(overdueFiltered);
      setIsInitialLoading(false);
      
      console.log(`Found ${dueTodayFiltered.length} due today, ${overdueFiltered.length} overdue for ${currentCompany.name}`);
    }, (error) => {
      console.error('Error loading returns:', error);
      setIsInitialLoading(false);
    });

    return () => unsubscribe();
  }, [companyId]);

  // Get bike details for display
  const getBikeDetails = (reservation) => {
    const bikeReference = reservation.motorbikeNumber;
    const details = bikeDetailsMap.get(bikeReference) || 
                   Array.from(bikeDetailsMap.values()).find(bike => 
                     bike.regNumber === bikeReference ||
                     bike.model === bikeReference ||
                     bike.id === bikeReference
                   );
    
    if (details) {
      return {
        model: details.model,
        regNumber: details.regNumber,
        dailyRate: details.dailyRate || 25,
        color: details.color,
        year: details.year,
        isManaged: true
      };
    }
    
    return {
      model: bikeReference || 'Unknown Model',
      regNumber: bikeReference || 'Unknown Registration',
      dailyRate: 25,
      isManaged: false
    };
  };

  // Calculate overdue days
  const calculateOverdueDays = (reservation) => {
    if (!reservation.dropoffDateTime) return 0;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const returnDate = new Date(reservation.dropoffDateTime);
    returnDate.setHours(0, 0, 0, 0);
    
    if (today > returnDate) {
      const diffTime = today - returnDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    
    return 0;
  };

  // ENHANCED: Create extended invoice using SHARED service
  const createExtendedInvoice = async (reservation, overdueDays) => {
    const bikeDetails = getBikeDetails(reservation);
    const extendedAmount = bikeDetails.dailyRate * overdueDays;
    
    // Set company context for SHARED service
    setCompanyContext(companyId, currentCompany.name);
    
    // Generate invoice number using SHARED service
    const invoiceNumber = await generateInvoiceNumber(companyId);
    
    console.log('📄 Creating extended invoice using SHARED firebaseInvoiceService');
    console.log('✅ Generated invoice number:', invoiceNumber);
    
    const today = new Date();
    const returnDate = new Date(reservation.dropoffDateTime);
    const extendedStartDate = new Date(returnDate);
    extendedStartDate.setDate(extendedStartDate.getDate() + 1);
    
    // Create invoice data using SHARED service format
    const invoiceData = {
      invoiceNumber,
      reservationId: reservation.id,
      customerName: reservation.customerName || "Unknown Customer",
      customerEmail: reservation.email || '',
      customerPhone: reservation.whatsappNumber || '',
      email: reservation.email || '',
      whatsappNumber: reservation.whatsappNumber || '',
      licenseNumber: reservation.licenseNumber || '',
      motorbikeNumber: reservation.motorbikeNumber,
      bikeModel: `${bikeDetails.model} (${bikeDetails.regNumber})`,
      totalAmount: extendedAmount,
      rentalAmount: extendedAmount,
      addonCosts: 0,
      discountAmount: 0,
      depositAmount: '0',
      pickupDateTime: extendedStartDate.toISOString(),
      dropoffDateTime: today.toISOString(),
      pickupLocation: reservation.pickupLocation || 'office',
      selectedAddons: [],
      paymentMethod: reservation.paymentMethod || 'Cash',
      rentalPaymentStatus: 'pending',
      status: 'generated',
      note: `Extended rental charges - ${overdueDays} days overdue. Original return date: ${returnDate.toLocaleDateString()}. Processed from Bike Returns.`,
      isExtendedRental: true,
      type: 'extended',
      automaticallyGenerated: false,
      originalReservationId: reservation.id,
      originalReturnDate: reservation.dropoffDateTime,
      daysOverdue: overdueDays,
      dailyRateApplied: bikeDetails.dailyRate,
      createdByUsername: currentUser?.username || 'System',
      createdBy: currentUser?.id || 'system',
      sourceComponent: 'bike-returns',
      processedBy: currentUser?.username || 'System',
      processedFrom: 'SimplifiedBikeReturns'
    };
    
    console.log('💾 Saving extended invoice to SHARED collection with company context:', companyId);
    
    // Use SHARED addInvoice service
    const invoiceId = await addInvoice(invoiceData, companyId, currentCompany.name);
    
    console.log('✅ Extended invoice created successfully with ID:', invoiceId);
    console.log('✅ Invoice will now appear in Invoice Management AND Finance Manager');
    
    return invoiceNumber;
  };

  // SIMPLIFIED: Handle return processing with SHARED invoice service
  const handleSimplifiedReturn = async (reservation, isOverdue = false) => {
    try {
      setIsLoading(true);
      console.log(`Processing simplified return for reservation: ${reservation.id} (${currentCompany.name})`);
      
      const bikeDetails = getBikeDetails(reservation);
      const customerName = reservation.customerName || 'Unknown';
      const overdueDays = isOverdue ? calculateOverdueDays(reservation) : 0;
      const extendedCost = isOverdue ? bikeDetails.dailyRate * overdueDays : 0;
      
      const confirmMessage = isOverdue ? 
        `Process OVERDUE return for ${currentCompany.name}:\n\n` +
        `Customer: ${customerName}\n` +
        `Bike: ${bikeDetails.model} (${bikeDetails.regNumber})\n` +
        `Overdue: ${overdueDays} days\n` +
        `Extended charges: $${extendedCost.toFixed(2)}\n\n` +
        `This will:\n` +
        `• Mark the reservation as completed\n` +
        `• Create extended charges invoice (via SHARED service)\n` +
        `• Invoice will appear in Invoice Management & Finance Manager` :
        `Process return for ${currentCompany.name}:\n\n` +
        `Customer: ${customerName}\n` +
        `Bike: ${bikeDetails.model} (${bikeDetails.regNumber})\n\n` +
        `This will mark the reservation as completed.`;
      
      if (!window.confirm(confirmMessage)) {
        setIsLoading(false);
        return;
      }
      
      // Step 1: Mark reservation as completed
      await updateDoc(doc(reservationsCollection, reservation.id), {
        rentalStatus: 'completed',
        availabilityStatus: 'completed',
        returnedAt: new Date().toISOString(),
        returnedBy: currentUser?.username || 'Unknown',
        isOverdue: isOverdue,
        daysOverdue: overdueDays,
        finalStatus: isOverdue ? 'returned_overdue' : 'returned_ontime',
        updatedAt: serverTimestamp()
      });
      
      // Step 2: Create extended charges invoice if overdue (using SHARED service)
      let invoiceNumber = null;
      if (isOverdue && overdueDays > 0) {
        try {
          invoiceNumber = await createExtendedInvoice(reservation, overdueDays);
          
          // Still record income entry in company-specific collection
          const today = new Date();
          const incomeEntry = {
            invoice: invoiceNumber,
            amount: extendedCost,
            date: today.toISOString().split('T')[0],
            description: `Extended Charges - ${customerName} (${overdueDays} days overdue)`,
            type: 'extended',
            isExtendedRental: true,
            companyId: companyId,
            processedBy: currentUser?.username || 'Unknown',
            createdAt: serverTimestamp()
          };
          
          await addDoc(incomeCollection, incomeEntry);
          
          alert(
            `Return processed successfully!\n\n` +
            `Extended charges invoice created: ${invoiceNumber}\n` +
            `Amount: $${extendedCost.toFixed(2)}\n` +
            `Reservation marked as completed.\n\n` +
            `✅ Invoice synced to Invoice Management & Finance Manager\n` +
            `✅ Using SHARED invoice service`
          );
        } catch (error) {
          console.error('Error creating extended invoice:', error);
          alert(
            `Reservation marked as completed, but invoice creation failed:\n${error.message}\n\n` +
            `Please create the extended invoice manually.`
          );
        }
      } else {
        alert(`Return processed successfully!\n\nReservation marked as completed for ${currentCompany.name}.`);
      }
      
      console.log(`Return completed for reservation ${reservation.id} in ${currentCompany.name}`);
      
    } catch (error) {
      console.error(`Error processing return:`, error);
      alert(`Error processing return: ${error.message}\nPlease try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center">
            <XCircle className="w-5 h-5 mr-2 text-orange-400" />
            Bike Returns
            <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
              Simplified
            </span>
          </h2>
        </div>
        
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-orange-500 mr-3" size={32} />
            <div>
              <h3 className="text-white font-medium">Loading Simplified Returns</h3>
              <p className="text-gray-400 text-sm">Single data source initialization...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lg:col-span-3 bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-700">
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <XCircle className="w-5 h-5 mr-2 text-orange-400" />
          Bike Returns
          <span className="ml-2 text-xs bg-blue-600 text-white px-2 py-1 rounded-full">
            Simplified
          </span>
          <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">
            Single Data Source
          </span>
        </h2>
        <p className="text-sm text-gray-400 mt-1">
          Simplified return processing using reservations data only
        </p>
        <div className="mt-2 flex items-center text-blue-400 text-sm">
          <Building size={16} className="mr-1" />
          <span>{currentCompany.name} • {bikes.length} bikes</span>
        </div>
      </div>
      
      {/* Simplified Architecture Panel */}
      <div className="mx-6 mt-6 bg-blue-900/30 border border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Database className="text-blue-400 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <h3 className="text-blue-300 font-medium mb-2 flex items-center gap-2">
              Simplified Returns System: {currentCompany.name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-200">Data Source:</span>
                <div className="text-gray-300">✓ Reservations only</div>
                <div className="text-gray-400 text-xs">No complex dependencies</div>
              </div>
              <div>
                <span className="text-blue-200">Due Today:</span>
                <div className="text-gray-300">{dueTodayReservations.length} returns</div>
                <div className="text-gray-400 text-xs">Based on dropoff date</div>
              </div>
              <div>
                <span className="text-blue-200">Overdue:</span>
                <div className="text-gray-300">{overdueReservations.length} returns</div>
                <div className="text-gray-400 text-xs">Auto-calculated</div>
              </div>
              <div>
                <span className="text-blue-200">Extended Invoices:</span>
                <div className="text-green-300">✓ SHARED service</div>
                <div className="text-gray-400 text-xs">Synced to all systems</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        {/* Due Today Section */}
        <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
          <div className="bg-amber-800 p-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              Due Today ({dueTodayReservations.length})
              <Database size={16} className="text-amber-200" />
              <span className="text-amber-200 text-sm">{currentCompany.name}</span>
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {dueTodayReservations.map((reservation, index) => {
              const bikeDetails = getBikeDetails(reservation);
              
              return (
                <div
                  key={reservation.id || `due-${index}`}
                  className="bg-gray-800 rounded-lg p-4 border border-amber-600"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-white flex items-center gap-2">
                      {bikeDetails.regNumber} - {bikeDetails.model}
                      {bikeDetails.isManaged ? (
                        <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                          Managed
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded-full">
                          Basic
                        </span>
                      )}
                    </span>
                    <span className="bg-amber-900 text-amber-400 text-xs px-2 py-1 rounded-full">Due Today</span>
                  </div>
                  <div className="text-sm text-gray-300 mb-1">
                    {reservation.customerName || "Unknown Customer"}
                  </div>
                  <div className="text-sm text-gray-300 mb-1">
                    {reservation.pickupDateTime ? new Date(reservation.pickupDateTime).toLocaleDateString() : "?"} - 
                    {reservation.dropoffDateTime ? new Date(reservation.dropoffDateTime).toLocaleDateString() : "?"}
                  </div>
                  <div className="text-sm text-gray-300 mb-2">Reservation ID: {reservation.id}</div>
                  
                  {/* Bike rate info */}
                  <div className="mb-3 p-2 bg-gray-900/50 border border-gray-600 rounded">
                    <div className="text-xs text-gray-400 mb-1">Bike Rate:</div>
                    <div className="text-green-300 text-sm font-medium">
                      ${bikeDetails.dailyRate}/day {bikeDetails.isManaged ? '(Managed)' : '(Default)'}
                    </div>
                  </div>

                  {/* Simplified data source indicator */}
                  <div className="mb-3 p-2 bg-blue-900/20 border border-blue-700 rounded text-center">
                    <div className="text-xs text-blue-300 flex items-center justify-center">
                      <Database size={12} className="mr-1" />
                      Simplified System • {currentCompany.name}
                    </div>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={() => handleSimplifiedReturn(reservation, false)}
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <Loader className="animate-spin mr-2" size={16} />
                        Processing...
                      </div>
                    ) : (
                      'Mark as Returned'
                    )}
                  </button>
                </div>
              );
            })}
            
            {dueTodayReservations.length === 0 && (
              <div className="text-center py-6 text-gray-400">
                <p>No returns due today for {currentCompany.name}</p>
                <p className="text-xs text-gray-500 mt-1">Returns will appear here when due</p>
                <div className="mt-3 p-2 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <div className="text-xs text-blue-300 flex items-center justify-center">
                    <Database size={12} className="mr-1" />
                    Simplified data from reservations collection
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Overdue Section */}
        <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
          <div className="bg-red-800 p-4">
            <h3 className="font-semibold text-white flex items-center gap-2">
              Overdue Returns ({overdueReservations.length})
              <Database size={16} className="text-red-200" />
              <span className="text-red-200 text-sm">{currentCompany.name}</span>
            </h3>
          </div>
          <div className="p-4 space-y-4">
            {overdueReservations.map((reservation, index) => {
              const bikeDetails = getBikeDetails(reservation);
              const overdueDays = calculateOverdueDays(reservation);
              const extendedCost = bikeDetails.dailyRate * overdueDays;
              
              return (
                <div
                  key={reservation.id || `overdue-${index}`}
                  className="bg-gray-800 rounded-lg p-4 border border-red-800"
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-white flex items-center gap-2">
                      {bikeDetails.regNumber} - {bikeDetails.model}
                      {bikeDetails.isManaged ? (
                        <span className="text-xs bg-green-600 text-white px-2 py-1 rounded-full">
                          Managed
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-600 text-white px-2 py-1 rounded-full">
                          Basic
                        </span>
                      )}
                    </span>
                    <span className="bg-red-900 text-red-400 text-xs px-2 py-1 rounded-full">
                      {overdueDays} {overdueDays === 1 ? 'Day' : 'Days'} Overdue
                    </span>
                  </div>
                  <div className="text-sm text-gray-300 mb-1">
                    {reservation.customerName || "Unknown Customer"}
                  </div>
                  <div className="text-sm text-gray-300 mb-1">
                    {reservation.pickupDateTime ? new Date(reservation.pickupDateTime).toLocaleDateString() : "?"} - 
                    {reservation.dropoffDateTime ? new Date(reservation.dropoffDateTime).toLocaleDateString() : "?"}
                  </div>
                  <div className="text-sm text-gray-300 mb-2">Reservation ID: {reservation.id}</div>
                  
                  {/* Bike rate and overdue calculation */}
                  <div className="mb-3 p-2 bg-gray-900/50 border border-gray-600 rounded">
                    <div className="text-xs text-gray-400 mb-1">Bike Rate:</div>
                    <div className="text-green-300 text-sm">
                      ${bikeDetails.dailyRate}/day {bikeDetails.isManaged ? '(Managed)' : '(Default)'}
                    </div>
                  </div>
                  
                  <div className="bg-red-900/30 py-2 px-3 rounded mb-2 text-sm text-red-300">
                    <span className="font-semibold">Extended charges: ${extendedCost.toFixed(2)}</span>
                    <div className="text-xs text-red-200 mt-1">
                      {overdueDays} days × ${bikeDetails.dailyRate}/day
                    </div>
                  </div>

                  {/* Simplified data source indicator */}
                  <div className="mb-3 p-2 bg-blue-900/20 border border-blue-700 rounded text-center">
                    <div className="text-xs text-blue-300 flex items-center justify-center">
                      <Database size={12} className="mr-1" />
                      Simplified System • SHARED Invoice Service
                    </div>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={() => handleSimplifiedReturn(reservation, true)}
                    disabled={isLoading}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <Loader className="animate-spin mr-2" size={16} />
                        Processing...
                      </div>
                    ) : (
                      'Process Overdue Return'
                    )}
                  </button>
                </div>
              );
            })}
            
            {overdueReservations.length === 0 && (
              <div className="text-center py-6 text-gray-400">
                <p>No overdue returns for {currentCompany.name}</p>
                <p className="text-xs text-gray-500 mt-1">Overdue rentals will appear here</p>
                <div className="mt-3 p-2 bg-blue-900/20 border border-blue-700 rounded-lg">
                  <div className="text-xs text-blue-300 flex items-center justify-center">
                    <Database size={12} className="mr-1" />
                    Simplified data from reservations collection
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 flex items-center border border-gray-700">
            <Loader className="animate-spin text-orange-500 mr-3" size={24} />
            <span className="text-white">Processing simplified return for {currentCompany.name}...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimplifiedBikeReturns;